import { Box, Heading, Tabs, Paragraph } from '@zillow/constellation';
import React from 'react';

import {
    ZestimateTable,
    ZestimateTableData,
} from '../ZestimateTable/ZestimateTable';

/**
 * Props for the ZestimateData component.
 */
export interface ZestimateDataProps {
    /** The zestimate data object containing multiple sections */
    data: Record<string, ZestimateTableData>;
    /** The heading title for this data set */
    title: string;
}

/**
 * ZestimateData component that displays Zestimate accuracy data in tabbed tables.
 * Each top-level key in the data object becomes a separate tab.
 *
 * @component
 * @example
 * ```tsx
 * <ZestimateData
 *   data={zestimateDataOnMarket}
 *   title="Active listings accuracy"
 * />
 * ```
 */
export function ZestimateData({
    data,
    title,
}: ZestimateDataProps): JSX.Element {
    return (
        <>
            <Heading
                level="3"
                fontType="h4"
                fontFamily="serif"
                textAlign="center"
                marginBottom="sm"
            >
                {title}
            </Heading>
            <Tabs
                defaultSelected={`${Object.keys(data)[0]
                    .toLowerCase()
                    .replace(/\s+/g, '-')}-tab`}
            >
                <Tabs.List
                    aria-label="Zestimate accuracy data by region"
                    justifyContent="center"
                >
                    {Object.keys(data).map((sectionName) => {
                        const tabId = `${sectionName
                            .toLowerCase()
                            .replace(/\s+/g, '-')}-tab`;
                        const panelId = `${sectionName
                            .toLowerCase()
                            .replace(/\s+/g, '-')}-panel`;

                        return (
                            <Tabs.Tab
                                key={tabId}
                                id={tabId}
                                aria-controls={panelId}
                            >
                                {sectionName}
                            </Tabs.Tab>
                        );
                    })}
                </Tabs.List>

                <Tabs.Panels>
                    {Object.entries(data).map(([sectionName, sectionData]) => {
                        const tabId = `${sectionName
                            .toLowerCase()
                            .replace(/\s+/g, '-')}-tab`;
                        const panelId = `${sectionName
                            .toLowerCase()
                            .replace(/\s+/g, '-')}-panel`;

                        return (
                            <Tabs.Panel
                                key={panelId}
                                id={panelId}
                                aria-labelledby={tabId}
                            >
                                <Box paddingTop="md">
                                    <ZestimateTable
                                        data={sectionData}
                                        ariaLabel={`${title} - ${sectionName}`}
                                    />
                                </Box>
                            </Tabs.Panel>
                        );
                    })}
                </Tabs.Panels>
            </Tabs>
            <Paragraph
                marginBottom="lg"
                paddingRight="sm"
                textAlign="right"
                fontType="legal"
            >
                Last updated: March 06, 2026
            </Paragraph>
        </>
    );
}
